import React, {ReactNode} from 'react';
import {NavLink} from 'react-router-dom';
import {TableSortDirection} from '~/components/Table';
import {renderAbbreviate, renderDollars, renderPercents} from '~/lib/helpers';
import {withCommas} from '~/lib/numbers';
import {sortByBoolean, sortByNumber, sortByString, SortFunction} from '~/lib/sort';
import {clientsLinks} from '~/modules/Clients';
import {DashboardAbility} from '~/modules/Dashboard/ability';
import {getPayerLabel} from '~/modules/Dashboard/lib/payer';
import {Granularity, IPerformance, PerformanceType} from '~/modules/Dashboard/models/Performance';
import {useDashboardReducer} from '~/modules/Dashboard/reducer';

export interface IPerformanceFieldOptions {
    key?: keyof IPerformance;
    title: string;
    isDictionary?: boolean;
    tableCell?: (performance: IPerformance) => ReactNode;
    color?: string;
    performanceType?: PerformanceType;
    sort?: SortFunction<IPerformance>;
    hideByAbility?: (ability: DashboardAbility) => boolean;
    tableFooter?: (data: IPerformance[]) => ReactNode;
    tableValue?: (value: number) => number;
    chartValue?: (value: number) => number;
}

export const performanceValue = (value: number, short: boolean) => short ? renderAbbreviate(value, 2, false, false) : withCommas(value);

export const PerformanceValue = ({value}: { value: number }) => {
    const {state: {table: {short}}} = useDashboardReducer();
    return <>{performanceValue(value, short)}</>;
};

export function sortByDimension<T>(records: T[], field: string, direction: TableSortDirection): T[] {
    return direction === TableSortDirection.ASC
        ? records.sort((a, b) => (a[field].label || '').localeCompare((b[field].label || '')))
        : records.sort((a, b) => (b[field].label || '').localeCompare((a[field].label || '')));
}

export const getGranularityDateFormat = (granularity: Granularity): Intl.DateTimeFormatOptions => {
    if (granularity === Granularity.MONTH) {
        return {
            month: 'short',
            year: 'numeric',
            timeZone: 'UTC'
        };
    } else if (granularity === Granularity.WEEK) {
        return {
            day: 'numeric',
            month: 'numeric',
            year: 'numeric',
            timeZone: 'UTC'
        };
    } else if (granularity === Granularity.DAILY) {
        return {
            day: 'numeric',
            month: 'numeric',
            year: 'numeric',
            timeZone: 'UTC'
        };
    } else if (granularity === Granularity.HOUR) {
        return {
            day: 'numeric',
            month: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: 'numeric',
            timeZone: 'UTC'
        };
    }
};

export const toGranularityDateString = (date: Date, granularity: Granularity) =>
    date.toLocaleString(undefined, getGranularityDateFormat(granularity));

const fixPercentsValue = (value: number) => Math.round(value / 0.01) / 100;

// Blank rather than "No" when the flag is absent: the backend omits it on rows that carry no agency, where PaaS is
// unknown rather than false.
export const formatIsPaas = (isPaas?: boolean | null): string =>
    isPaas === undefined || isPaas === null ? '' : isPaas ? 'Yes' : 'No';

const formatPayerDisplay = (payer?: string | null): string => {
    const label = getPayerLabel(payer);
    if (!label) {
        return '';
    }
    const normalized = label.toLowerCase();
    if (normalized === 'google') {
        return 'Google';
    }
    if (normalized === 'bidmachine') {
        return 'BidMachine';
    }
    return label;
};

export const performanceFields: IPerformanceFieldOptions[] = [
    {
        key: 'country',
        title: 'Country',
        isDictionary: true,
        tableCell: performance => performance.country && performance.country.label,
        performanceType: PerformanceType.COUNTRY,
        sort: sortByDimension,
    },
    {
        key: 'adType',
        title: 'Ad Type',
        isDictionary: true,
        tableCell: performance => performance.adType && performance.adType.label,
        performanceType: PerformanceType.AD_TYPE,
        sort: sortByDimension
    },
    {
        key: 'agency',
        title: 'Agency',
        isDictionary: true,
        tableCell: performance => performance.agency &&
            <NavLink to={clientsLinks.agency(performance.agency.value)}>{performance.agency.label}</NavLink>,
        performanceType: PerformanceType.AGENCY,
        sort: sortByDimension
    },
    {
        key: 'seller',
        title: 'Seller',
        isDictionary: true,
        tableCell: performance => performance.seller &&
            <NavLink to={clientsLinks.seller(performance.seller.value)}>{performance.seller.label}</NavLink>,
        performanceType: PerformanceType.SELLER,
        sort: sortByDimension
    },
    {
        key: 'platform',
        title: 'Platform',
        isDictionary: true,
        tableCell: performance => performance.platform && performance.platform.label,
        performanceType: PerformanceType.PLATFORM,
        sort: sortByString
    },
    {
        key: 'payer',
        title: 'Payer',
        isDictionary: true,
        tableCell: performance => formatPayerDisplay(performance.payer),
        performanceType: PerformanceType.PAYER,
        sort: sortByString
    },
    {
        key: 'isPaas',
        title: 'PaaS',
        isDictionary: true,
        tableCell: performance => formatIsPaas(performance.isPaas),
        performanceType: PerformanceType.PAYER,
        sort: sortByBoolean
    },
    // Separate from `payer` on purpose: the payer collapses every PaaS agency into BidMachine and the
    // Google branch into Google, so it cannot answer who the money is actually with.
    {
        key: 'demandPartner',
        title: 'Demand Partner',
        isDictionary: true,
        tableCell: performance => performance.demandPartner ?? '',
        performanceType: PerformanceType.PAYER,
        sort: sortByString
    },
    {
        key: 'adRequests',
        title: 'Ad requests',
        hideByAbility: ability => !ability.canViewAdRequests(),
        tableCell: performance => <PerformanceValue value={performance.adRequests}/>,
        sort: sortByNumber,
        color: '#FFBB28',
        tableFooter: data => <PerformanceValue
            value={data.reduce((summ, performance) => summ + performance.adRequests, 0)}/>
    },
    {
        key: 'impressions',
        title: 'Impressions',
        tableCell: performance => <PerformanceValue value={performance.impressions}/>,
        sort: sortByNumber,
        color: '#1F77B4',
        tableFooter: data => <PerformanceValue
            value={data.reduce((summ, performance) => summ + performance.impressions, 0)}/>
    },
    {
        key: 'finishes',
        title: 'Finishes',
        hideByAbility: ability => ability.isBuyer(),
        tableCell: performance => <PerformanceValue value={performance.finishes}/>,
        sort: sortByNumber,
        color: '#2CA02C',
        tableFooter: data => <PerformanceValue
            value={data.reduce((summ, performance) => summ + performance.finishes, 0)}/>
    },
    {
        key: 'clicks',
        title: 'Clicks',
        tableCell: performance => <PerformanceValue value={performance.clicks}/>,
        sort: sortByNumber,
        color: '#FF7F0E',
        tableFooter: data => <PerformanceValue
            value={data.reduce((summ, performance) => summ + performance.clicks, 0)}/>
    },
    {
        key: 'ctr',
        title: 'CTR, %',
        tableCell: performance => renderPercents(performance.ctr),
        sort: sortByNumber,
        color: '#9467BD',
        tableFooter: data => {
            const clicks = data.reduce((s, p) => s + p.clicks, 0);
            const impressions = data.reduce((s, p) => s + p.impressions, 0);
            return renderPercents(impressions > 0 ? (clicks / impressions) * 100 : 0);
        },
        chartValue: fixPercentsValue
    },
    {
        key: 'spent',
        title: 'Spent, $',
        tableCell: performance => renderDollars(performance.spent),
        sort: sortByNumber,
        color: '#8884D8',
        hideByAbility: ability => ability.isSeller() || ability.isSelfServe(),
        tableFooter: data => renderDollars(data.reduce((summ, performance) => summ + performance.spent, 0))
    },
    {
        key: 'ecpm',
        title: 'eCPM, $',
        tableCell: performance => renderDollars(performance.ecpm),
        sort: sortByNumber,
        color: '#8C564B',
        tableFooter: data => {
            const impressions = data.reduce((s, p) => s + p.impressions, 0);
            const ecpmSum = data.reduce((s, p) => s + p.ecpm, 0);
            return renderDollars((impressions > 0 && data.length > 0) ? ecpmSum / data.length : 0);
        }
    },
    {
        key: 'sspIncome',
        title: 'Estimated income',
        hideByAbility: ability => ability.isBuyer(),
        tableCell: performance => renderDollars(performance.sspIncome),
        sort: sortByNumber,
        color: '#E377C2',
        tableFooter: data => renderDollars(data.reduce((summ, performance) => summ + performance.sspIncome, 0))
    },
    {
        key: 'exchangeFee',
        title: 'Exchange Fee, $',
        tableCell: performance => renderDollars(performance.exchangeFee),
        sort: sortByNumber,
        color: '#BCBD22',
        hideByAbility: ability => ability.isSeller() || ability.isBuyer() || ability.isSelfServe(),
        tableFooter: data => renderDollars(data.reduce((summ, performance) => summ + performance.exchangeFee, 0))
    },
    {
        key: 'exchangeFeePercentage',
        title: 'Fee, %',
        tableCell: performance => renderPercents(performance.exchangeFeePercentage),
        sort: sortByNumber,
        color: '#BAAD22',
        hideByAbility: ability => !(ability.isAdmin() || ability.isManager()),
        tableFooter: data => {
            const exchangeFee = data.reduce((s, p) => s + p.exchangeFee, 0);
            const spent = data.reduce((s, p) => s + p.spent, 0);
            return renderPercents(spent > 0 ? (exchangeFee / spent * 100) : 0);
        }
    },
    {
        key: 'lostImpressions',
        title: 'Lost impressions',
        tableCell: performance => <PerformanceValue value={performance.lostImpressions}/>,
        sort: sortByNumber,
        color: '#00C49F',
        hideByAbility: ability => ability.isBuyer() || ability.isSeller() || ability.isSelfServe(),
        tableFooter: data => <PerformanceValue
            value={data.reduce((summ, performance) => summ + performance.lostImpressions, 0)}/>
    },
    {
        key: 'lostImpressionsRevenue',
        title: 'Lost impressions revenue',
        hideByAbility: ability => ability.isBuyer() || ability.isSeller() || ability.isSelfServe(),
        tableCell: performance => renderDollars(performance.lostImpressionsRevenue),
        sort: sortByNumber,
        color: '#FFBB28',
        tableFooter: data => renderDollars(data.reduce((summ, performance) => summ + performance.lostImpressionsRevenue, 0))
    },
    {
        key: 'errors',
        title: 'Errors',
        hideByAbility: ability => ability.isBuyer() || ability.isSeller() || ability.isSelfServe(),
        tableCell: performance => <PerformanceValue value={performance.errors}/>,
        sort: sortByNumber,
        color: '#17BECF',
        tableFooter: data => <PerformanceValue
            value={data.reduce((summ, performance) => summ + performance.errors, 0)}/>
    }
];

export const getPerformanceField = (field: keyof IPerformance): IPerformanceFieldOptions =>
    performanceFields.find(fieldOptions => fieldOptions.key === field);

export const getVisibleFieldsWithStorage = (
    storageKey: string,
    defaultFields?: (keyof IPerformance)[]): (keyof IPerformance)[] =>
    JSON.parse(localStorage.getItem(`performance.${storageKey}.visibleField`)) || defaultFields;

export const setVisibleFieldsWithStorage = (
    visibleFields: (keyof IPerformance)[],
    storageKey: string,
    callback: (visibleFields: (keyof IPerformance)[]) => void) => {
    localStorage.setItem(`performance.${storageKey}.visibleField`, JSON.stringify(visibleFields as any));
    callback(visibleFields);
};
